"use client";

import React from "react";
import { ArrowRight, Zap, Smartphone, UserCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSnapExperience() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    id: "lighting",
    title: "Bulwagan Bamboo Hall",
    description: "A breathtaking bamboo sanctuary overlooking rice fields, perfect for weddings and grand celebrations of up to 50 guests. Experience the magic of Peliatan's natural beauty.",
    icon: <Sparkles className="size-8 text-secondary drop-shadow-[0_0_10px_var(--color-secondary)]" />,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/snapbooth_pro_lighting_setup.png",
    colSpan: "md:col-span-2 md:row-span-2",
    bgClass: "bg-gradient-to-br from-background to-background/50",
    glowColor: "group-hover:border-secondary/30 group-hover:bg-accent/40 group-hover:shadow-[0_0_30px_-5px_var(--color-secondary)]"
  }, {
    id: "sharing",
    title: "Kilig Klub Space",
    description: "A flexible creative space designed for intimate workshops, kids' parties, and community gatherings in a comfortable setting.",
    icon: <Smartphone className="size-6 text-secondary drop-shadow-[0_0_8px_var(--color-secondary)]" />,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/snapbooth_mobile_sharing_mockup.png",
    colSpan: "md:col-span-1 md:row-span-1",
    bgClass: "bg-background/80",
    glowColor: "group-hover:border-secondary/30 group-hover:bg-accent/40 group-hover:shadow-[0_0_20px_-5px_var(--color-secondary)]"
  }, {
    id: "staff",
    title: "Kilig Sala Dining",
    description: "An intimate dining room for 10–15 guests, offering private rice field views and curated menus for special moments.",
    icon: <UserCheck className="size-6 text-secondary drop-shadow-[0_0_8px_var(--color-secondary)]" />,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/snapbooth_technician_friendly.png",
    colSpan: "md:col-span-1 md:row-span-1",
    bgClass: "bg-background/80",
    glowColor: "group-hover:border-secondary/30 group-hover:bg-accent/40 group-hover:shadow-[0_0_20px_-5px_var(--color-secondary)]"
  }];
  return <section data-section-id="10500" id="celebrate-every-milestone" ref={ref} className={`relative w-full py-24 overflow-hidden bg-background transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-medium mb-4">
            <Sparkles className="size-4" />
            <span>Celebrate Every Milestone</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-4">
            More Than Just a Venue
          </h2>
          <p className="text-muted-foreground text-lg">
            We blend traditional bamboo architecture with modern hospitality to create an unforgettable interactive experience in Ubud.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[minmax(300px,auto)]">
          {features.map((feature, i) => <Card key={feature.id} data-index={i} className={`group relative overflow-hidden border-border/50 backdrop-blur-sm transition-all duration-500 ${feature.colSpan} ${feature.bgClass} ${feature.glowColor} hover:-translate-y-1`}>
              {}
              <div className="absolute inset-0 z-0">
                <img src={feature.image} alt={feature.title} className="w-full h-full object-cover opacity-30 group-hover:opacity-20 transition-opacity duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
              </div>

              <CardHeader className="relative z-10 flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="p-3 rounded-xl bg-background/50 border border-white/10 backdrop-blur-md">
                  {feature.icon}
                </div>
                <div className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out">
                  <Button variant="ghost" size="icon" className="rounded-full bg-secondary/10 text-secondary hover:bg-secondary/20 hover:text-secondary" asChild>
                    <Link to={feature.id === "lighting" ? "/BambooHallPage" : feature.id === "sharing" ? "/WorkshopSpacePage" : "/PrivateDiningPage"} aria-label={`Learn more about ${feature.title}`}>
                      <ArrowRight className="size-5" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="relative z-10 mt-auto pt-12">
                <CardTitle className="text-2xl md:text-3xl font-bold text-foreground mb-3 group-hover:text-secondary transition-colors duration-300">
                  {feature.title}
                </CardTitle>
                <CardDescription className="text-base md:text-lg text-muted-foreground/90 group-hover:text-foreground transition-colors duration-300">
                  {feature.description}
                </CardDescription>
                
                <div className="mt-6 h-0 overflow-hidden group-hover:h-8 transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100">
                   <Link to={feature.id === "lighting" ? "/BambooHallPage" : feature.id === "sharing" ? "/WorkshopSpacePage" : "/PrivateDiningPage"} className="inline-flex items-center text-sm font-medium text-secondary hover:text-secondary/80 transition-colors">
                      Learn More <ArrowRight className="ml-2 size-4" />
                   </Link>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}